/**
 * Custom hook untuk mengambil kandidat merge (pasangan segmen split + status
 * kelayakan merge) berdasarkan booking_room_id.
 *
 * API Endpoint: GET `/api/v4/booking/merge-reservation-candidate`
 * API Version: 4.0
 */

import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { privateApi } from "../../../../api/api";
import { MergeCandidateSchema } from "../../schema/MergeReservationSchema";

const API_VERSION = "v4";

const MergeCandidateResponseSchema = z.object({
    success: z.boolean(),
    // responseSuccess() mengirim `message: null` kalau endpoint tidak menyetel
    // pesan -- persis yang dilakukan endpoint candidate ini.
    message: z.string().nullable(),
    code: z.number(),
    data: MergeCandidateSchema,
});

interface useMergeReservationCandidateProps {
    bookingRoomId: string;
    enabled?: boolean;
}

const useMergeReservationCandidate = ({ bookingRoomId, enabled = true }: useMergeReservationCandidateProps) => {
    return useQuery({
        queryKey: ['merge-candidate', bookingRoomId],
        queryFn: async () => {
            const response = await privateApi.get(`/${API_VERSION}/booking/merge-reservation-candidate`, {
                params: { booking_room_id: bookingRoomId },
            });

            const validationResult = MergeCandidateResponseSchema.safeParse(response.data);
            if (!validationResult.success) {
                console.error("Validation failed:", validationResult.error.errors);
                throw new Error("Invalid merge candidate data format");
            }
            return validationResult.data;
        },
        enabled: enabled && !!bookingRoomId,
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false,
    });
};

export default useMergeReservationCandidate;
